import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Heart, X, Waves, Bell, Mail } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useApp } from "@/context/AppContext";
import { useFavorites } from "@/context/FavoritesContext";
import { getProperties, createAlert } from "@/lib/api";
import { euro, providerName, PROVIDER_META } from "@/lib/format";
import { BrandTag } from "@/components/BrandTag";
import { T } from "@/i18n/translations";

const PROVIDERS = ["nuitee", "holidu", "booking", "direct"];

export default function Compare() {
  const { t, lang, config } = useApp();
  const { favorites, toggle, clear, alerts, setAlert } = useFavorites();
  const [items, setItems] = useState([]);
  const [emails, setEmails] = useState({});
  const [phones, setPhones] = useState({});
  const notified = useRef(new Set());

  useEffect(() => {
    if (!favorites.length) {
      setItems([]);
      return;
    }
    getProperties({ slugs: favorites.join(","), limit: 100 })
      .then((data) => setItems(data))
      .catch(() => {});
  }, [favorites]);

  useEffect(() => {
    items.forEach((p) => {
      const tgt = alerts[p.slug];
      if (tgt && p.best_price != null && p.best_price <= tgt) {
        const key = `${p.slug}:${tgt}`;
        if (!notified.current.has(key)) {
          notified.current.add(key);
          toast.success(`${T.compare.alert_toast[lang]} ${p.name} — ${euro(p.best_price)}`);
        }
      }
    });
  }, [items, alerts, lang]);

  const c = (k) => T.compare[k][lang];

  const subscribeEmail = async (p) => {
    const email = emails[p.slug];
    const target = alerts[p.slug];
    if (!email || !target) return;
    try {
      const res = await createAlert({ slug: p.slug, email, target_price: Number(target), phone: phones[p.slug] || undefined, lang });
      toast.success(res.triggered ? c("alert_hit") : `${c("alert_email_ok")} ${email}`);
    } catch (e) {
      toast.error(c("alert_email_err"));
    }
  };

  const Row = ({ label, render }) => (
    <tr className="border-t border-sand-border">
      <td className="sticky left-0 z-10 bg-sand-card px-4 py-3 text-xs font-semibold uppercase tracking-wide text-petrol/60">
        {label}
      </td>
      {items.map((p) => (
        <td key={p.slug} className="min-w-[220px] px-4 py-3 align-top">
          {render(p)}
        </td>
      ))}
    </tr>
  );

  return (
    <div className="min-h-screen overflow-x-hidden bg-sand">
      <Navbar solid />
      <div className="mx-auto max-w-7xl px-5 pb-20 pt-28 lg:px-8">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="overline text-terracotta">{c("over")}</p>
            <h1 className="mt-1 font-serif text-4xl font-medium text-petrol lg:text-5xl">{c("title")}</h1>
          </div>
          {items.length > 0 && (
            <button
              onClick={clear}
              data-testid="compare-clear"
              className="flex items-center gap-2 rounded-none border border-sand-border bg-white px-4 py-2.5 text-sm font-medium text-petrol hover:border-terracotta"
            >
              <X size={15} /> {c("clear")}
            </button>
          )}
        </div>

        {items.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-sand-border bg-sand-card py-24 text-center"
          >
            <Heart size={40} className="text-terracotta" />
            <h2 className="mt-4 font-serif text-2xl text-petrol">{c("empty_title")}</h2>
            <p className="mt-2 max-w-sm text-petrol/60">{c("empty_sub")}</p>
            <Link
              to="/stays"
              className="mt-6 rounded-none bg-terracotta px-6 py-3 font-semibold text-white transition-colors hover:bg-terracotta-hover"
            >
              {c("browse")}
            </Link>
          </motion.div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-sand-border bg-sand-card no-scrollbar" data-testid="compare-table">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 bg-sand-card px-4 py-4" />
                  {items.map((p) => (
                    <th key={p.slug} className="min-w-[220px] px-4 py-4 align-top">
                      <div className="relative overflow-hidden rounded-xl">
                        <img src={p.images?.[0]} alt={p.name} className="h-28 w-full object-cover" />
                        <button
                          onClick={() => toggle(p.slug)}
                          data-testid={`compare-remove-${p.slug}`}
                          className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-petrol hover:text-terracotta"
                        >
                          <X size={14} />
                        </button>
                      </div>
                      <Link to={`/stay/${p.slug}`} className="mt-2 block font-serif text-lg font-semibold leading-tight text-petrol hover:text-terracotta">
                        {p.name}
                      </Link>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <Row label={c("best")} render={(p) => (
                  <span className="font-serif text-2xl font-bold text-petrol">{euro(p.best_price)}<span className="text-xs font-normal text-petrol/50">{t("card.per_night")}</span></span>
                )} />
                <Row label={c("rating")} render={(p) => (
                  <span className="flex items-center gap-1.5 text-petrol">
                    <span className="rounded-md bg-emerald-600 px-2 py-0.5 text-sm font-bold text-white">{p.rating}</span>
                    <span className="text-xs text-petrol/60">{p.reviews_count} {t("card.reviews")}</span>
                  </span>
                )} />
                <Row label={c("stars")} render={(p) => (
                  <span className="flex gap-0.5">{Array.from({ length: p.stars }).map((_, i) => <Star key={i} size={13} className="fill-terracotta text-terracotta" />)}</span>
                )} />
                <Row label={c("zone")} render={(p) => <span className="text-petrol">{p.zone_name}</span>} />
                <Row label={c("beach")} render={(p) => (
                  <span className="flex items-center gap-1.5 text-petrol"><Waves size={13} /> {p.distance_beach}m</span>
                )} />
                {PROVIDERS.map((k) => (
                  <Row key={k} label={k === "nuitee" ? <BrandTag compact className="normal-case" /> : providerName(k)} render={(p) => {
                    const val = p.prices?.[k];
                    const isBest = k === p.best_provider;
                    return val == null ? (
                      <span className="text-xs italic text-petrol/40">{t("detail.coming_soon")}</span>
                    ) : (
                      <span className={`flex items-center gap-1.5 font-mono ${isBest ? "font-bold text-emerald-700" : "text-petrol"}`}>
                        <span className="h-2 w-2 rounded-full" style={{ background: PROVIDER_META[k].color }} />
                        {euro(val)}
                        {((k === "nuitee" && p.live_pricing) || (k === "booking" && p.booking_live)) && (
                          <span data-testid="live-price-badge" className="flex items-center gap-1 rounded bg-emerald-100 px-1 text-[0.55rem] font-bold uppercase text-emerald-700">
                            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
                            {t("card.live")}
                          </span>
                        )}
                      </span>
                    );
                  }} />
                ))}
                <Row
                  label={<span className="flex items-center gap-1.5"><Bell size={12} /> {c("alert_set")}</span>}
                  render={(p) => {
                    const tgt = alerts[p.slug] || "";
                    const hit = tgt && p.best_price != null && p.best_price <= Number(tgt);
                    return (
                      <div className="flex flex-col gap-1.5">
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-petrol/50">€</span>
                          <input
                            type="number"
                            min="0"
                            data-testid={`alert-input-${p.slug}`}
                            value={tgt}
                            onChange={(e) => setAlert(p.slug, e.target.value)}
                            placeholder="—"
                            className="w-20 rounded-md border border-sand-border bg-white px-2 py-1 text-sm outline-none focus:border-terracotta"
                          />
                        </div>
                        {tgt ? (
                          <span data-testid={`alert-status-${p.slug}`} className={`text-xs font-semibold ${hit ? "text-emerald-600" : "text-petrol/50"}`}>
                            {hit ? c("alert_hit") : `${c("alert_wait")} €${tgt}`}
                          </span>
                        ) : null}
                        {tgt ? (
                          <div className="mt-1 flex flex-col gap-1.5">
                            <input
                              type="email"
                              data-testid={`alert-email-${p.slug}`}
                              value={emails[p.slug] || ""}
                              onChange={(e) => setEmails((s) => ({ ...s, [p.slug]: e.target.value }))}
                              placeholder={c("alert_email_ph")}
                              className="w-40 rounded-md border border-sand-border bg-white px-2 py-1 text-xs outline-none focus:border-terracotta"
                            />
                            {config?.whatsapp_enabled && (
                              <input
                                type="tel"
                                data-testid={`alert-phone-${p.slug}`}
                                value={phones[p.slug] || ""}
                                onChange={(e) => setPhones((s) => ({ ...s, [p.slug]: e.target.value }))}
                                placeholder={c("alert_phone_ph")}
                                className="w-40 rounded-md border border-sand-border bg-white px-2 py-1 text-xs outline-none focus:border-terracotta"
                              />
                            )}
                            <button
                              data-testid={`alert-email-btn-${p.slug}`}
                              onClick={() => subscribeEmail(p)}
                              disabled={!emails[p.slug]}
                              className="flex w-max items-center gap-1.5 rounded-none bg-petrol px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-terracotta disabled:opacity-40"
                            >
                              <Mail size={12} /> {c("alert_email_btn")}
                            </button>
                          </div>
                        ) : null}
                      </div>
                    );
                  }}
                />
                <Row label="" render={(p) => (
                  <Link to={`/stay/${p.slug}`} className="inline-block rounded-none bg-petrol px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-terracotta">
                    {c("view")}
                  </Link>
                )} />
              </tbody>
            </table>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
